import React, {useCallback, useEffect, useMemo, useRef, useState, forwardRef} from 'react';
import { useRafInterval } from 'ahooks';
import { useUserContext } from "../../contexts";
import { searchAssets } from '../../lib/api';
import useHeaderMenu from './useHeaderMenu';

import './SearchBar.scss';

const MAX_AUTOCOMPLETE_MATCHES = 10;
const LOCAL_DEV_HOSTNAMES = ['localhost', '127.0.0.1'];
const INTERNAL_MENU_HOSTS = new Set([
    'productioncrate.com',
    'www.productioncrate.com',
    'user.productioncrate.com',
    'user-qa.productioncrate.com',
    'staging.productioncrate.com',
    'app.productioncrate.com',
    'vfx.productioncrate.com',
    'graphics.productioncrate.com',
    'sfx.productioncrate.com',
    'rendercrate.com',
    'footagecrate.com',
    'graphicscrate.com',
    'soundscrate.com',
]);
const SEARCH_CATEGORIES = ['all', 'vfx', 'sfx', '3d', 'graphics'];

export const getCategoryFromLocation = () => {
    const path = window?.location?.pathname || '';
    const hostname = window?.location?.hostname || '';

    if (path.includes('/search')) {
        const mainCategory = new URLSearchParams(window.location.search).get('main_category');
        if (SEARCH_CATEGORIES.includes(mainCategory)) {
            return mainCategory;
        }
    }

    if (path.includes('/vfx') || hostname.includes('footagecrate')) {
        return 'vfx';
    }
    if (path.includes('/sfx') || hostname.includes('soundscrate')) {
        return 'sfx';
    }
    if (path.includes('/3d') || hostname.includes('rendercrate')) {
        return '3d';
    }
    if (path.includes('/graphics') || hostname.includes('graphicscrate')) {
        return 'graphics';
    }

    return 'all';
};

const isLocalDevelopmentOrigin = (origin) => {
    try {
        const parsed = new URL(origin);
        return LOCAL_DEV_HOSTNAMES.includes(parsed.hostname) || parsed.hostname.endsWith('.localhost');
    } catch (error) {
        return false;
    }
};

const rewriteMenuUrlToCurrentOrigin = (url, currentOrigin) => {
    if (!url || !isLocalDevelopmentOrigin(currentOrigin)) {
        return url;
    }

    if (url.startsWith('mailto:')) {
        return url;
    }

    try {
        const parsed = new URL(url);
        if (!INTERNAL_MENU_HOSTS.has(parsed.hostname)) {
            return url;
        }
        return `${currentOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (error) {
        return url;
    }
};

const rewritePluginsMenuUrlToProductionCrate = (url) => {
    if (!url || url.startsWith('mailto:')) {
        return url;
    }

    try {
        const parsed = new URL(url);
        parsed.protocol = 'https:';
        parsed.hostname = 'www.productioncrate.com';
        return parsed.toString();
    } catch (error) {
        return url;
    }
};

const resolveMenuLink = (str, loggedIn, vertical, category, subcategory, customLink=false) => {
    const locationOrigin = window.location.origin || 'https://www.productioncrate.com';
    const linkSource = customLink || str;

    if (vertical === 'plugins' || category === 'plugins') {
        return rewritePluginsMenuUrlToProductionCrate(linkSource);
    }

    if (vertical !== 'plugins' && category !== 'plugins' && vertical !== 'home') {
        let uriPath = locationOrigin;

        switch (vertical) {
            case '3d':
                uriPath = 'https://rendercrate.com';
                break;
            case 'graphics':
                uriPath = 'https://graphicscrate.com';
                break;
            case 'vfx/motion-graphics':
            case 'vfx/visual-graphics':
                uriPath = 'https://footagecrate.com';
                break;
            case 'sfx':
                uriPath = 'https://soundscrate.com';
                break;
            default:
                break;
        }

        if (loggedIn) {
            uriPath = 'https://www.productioncrate.com';
        }

        if (locationOrigin.includes('productioncrate.com')) {
            switch (vertical) {
                case '3d':
                    uriPath = 'https://www.productioncrate.com';
                    break;
                case 'graphics':
                    uriPath = 'https://graphics.productioncrate.com';
                    break;
                case 'vfx/motion-graphics':
                case 'vfx/visual-graphics':
                    uriPath = 'https://vfx.productioncrate.com';
                    break;
                case 'sfx':
                    uriPath = 'https://sfx.productioncrate.com';
                    break;
                default:
                    break;
            }
        }

        if (isLocalDevelopmentOrigin(locationOrigin)) {
            uriPath = locationOrigin;
        }

        if (locationOrigin.includes('staging.') || locationOrigin.includes('user-qa.')) {
            uriPath = 'https://staging.productioncrate.com';
        }

        if (vertical && category && subcategory && category === 'search') {
            let searchVertical = vertical;
            if (vertical === 'vfx/motion-graphics' || vertical === 'vfx/visual-graphics') {
                searchVertical = 'vfx';
            }
            uriPath += `/search/${subcategory}?main_category=${searchVertical}`;
            return uriPath.toLocaleLowerCase();
        }

        if (vertical) {
            uriPath += `/${vertical}`;
        }
        if (category && category !== 'view-all') {
            uriPath += `/${category}`;
        }

        if (subcategory) {
            uriPath += `/${subcategory}`;
        }

        if (customLink) {
            return rewriteMenuUrlToCurrentOrigin(customLink, locationOrigin);
        }
        return rewriteMenuUrlToCurrentOrigin(uriPath.toLocaleLowerCase(), locationOrigin);
    }

    let finalUrl = !loggedIn ? str : str.replace('rendercrate.com', 'productioncrate.com')
        .replace('graphicscrate.com', 'graphics.productioncrate.com')
        .replace('footagecrate.com', 'vfx.productioncrate.com')
        .replace('soundscrate.com', 'sfx.productioncrate.com');

    if (finalUrl === 'https://rendercrate.com/3D') {
        finalUrl = 'https://rendercrate.com/';
    }
    if (finalUrl === 'https://productioncrate.com/3D') {
        finalUrl = 'https://productioncrate.com/3D/';
    }

    return rewriteMenuUrlToCurrentOrigin(finalUrl, locationOrigin);
};

const normalizeMainCategoryLabel = (mainCategory) => {
    const normalizedId = String(mainCategory?.id || '').toLowerCase();
    if (normalizedId === '3d') {
        return '3D';
    }
    return String(mainCategory?.name || '').trim();
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getSearchTokens = (query) => String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

const normalizeSearchQuery = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const findExactPluginMenuMatch = (menuEntries, query) => {
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery) {
        return null;
    }

    return menuEntries.find((menuEntry) => (
        menuEntry?.isPluginCategory
        && normalizeSearchQuery(menuEntry.name) === normalizedQuery
    )) || null;
};

const getSearchableMenuValues = (menuEntry) => [
    menuEntry?.name,
    menuEntry?.id,
    ...(Array.isArray(menuEntry?.keywords) ? menuEntry.keywords : []),
]
    .map((value) => String(value || '').toLowerCase())
    .filter(Boolean);

const calculateMatchScore = (menuEntry, queryTokens) => {
    const searchableValues = getSearchableMenuValues(menuEntry);

    let score = menuEntry?.isPluginCategory ? 1000 : 0;

    queryTokens.forEach((token) => {
        searchableValues.forEach((value, index) => {
            const nameWeight = index === 0 ? 1 : 0.8;
            if (value.startsWith(token)) score += 30 * nameWeight;
            if (value.includes(token)) score += 20 * nameWeight;
        });
    });

    return score;
};

const renderHighlightedText = (text, queryTokens) => {
    const value = String(text || '');
    if (!value || !Array.isArray(queryTokens) || queryTokens.length === 0) {
        return value;
    }

    const sortedTokens = [...new Set(queryTokens)].sort((a, b) => b.length - a.length);
    if (sortedTokens.length === 0) {
        return value;
    }

    const regex = new RegExp(`(${sortedTokens.map((token) => escapeRegExp(token)).join('|')})`, 'ig');
    const parts = value.split(regex);

    return parts.map((part, index) => {
        const isMatch = sortedTokens.some((token) => part.toLowerCase() === token);
        if (!isMatch) {
            return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
        }
        return <span key={`match-${index}`} className="search-bar-autocomplete-match">{part}</span>;
    });
};

const resolveAutocompleteDisplayLabel = (menuMatch) => {
    const groupPath = String(menuMatch?.groupPath || '')
        .split('/')
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' / ');
    const name = String(menuMatch?.name || '').trim();
    if (!groupPath) {
        return name;
    }
    if (!name) {
        return groupPath;
    }
    if (groupPath.toLowerCase().endsWith(name.toLowerCase())) {
        return groupPath;
    }
    return `${groupPath} / ${name}`;
};

const buildGroupPath = (...segments) => segments
    .map((segment) => String(segment || '').trim())
    .filter(Boolean)
    .join(' / ');

const buildMenuAutocompleteSource = (headerMenu, loggedIn) => {
    const mainCategories = Array.isArray(headerMenu?.mainCategories) ? headerMenu.mainCategories : [];
    const menuEntries = [];
    const seenEntries = new Set();

    mainCategories.forEach((mainCategory) => {
        if (String(mainCategory?.id || '').toLowerCase() === 'home') {
            return;
        }

        const categoryLabel = normalizeMainCategoryLabel(mainCategory);
        if (!categoryLabel) {
            return;
        }

        const vertical = mainCategory?.slug ?? mainCategory?.id;
        const isPluginCategory = String(mainCategory?.id || '').toLowerCase() === 'plugins';

        if (mainCategory?.name && mainCategory?.link) {
            const mainCategoryLink = resolveMenuLink(mainCategory.link, loggedIn, mainCategory.id);
            const mainKey = `${categoryLabel}|${mainCategory.name}|${mainCategoryLink}`;
            if (!seenEntries.has(mainKey)) {
                seenEntries.add(mainKey);
                menuEntries.push({
                    id: String(mainCategory.id || mainCategory.name).trim(),
                    name: String(mainCategory.name).trim(),
                    link: mainCategoryLink,
                    category: categoryLabel,
                    groupPath: categoryLabel,
                    isPluginCategory,
                });
            }
        }

        const categories = Array.isArray(mainCategory?.categories) ? mainCategory.categories : [];

        categories.forEach((category) => {
            if (category?.name && category?.link) {
                const categoryLink = resolveMenuLink(
                    category.link,
                    loggedIn,
                    vertical,
                    category.id,
                    null,
                    category.customLink ? category.link : false,
                );
                const categoryKey = `${categoryLabel}|${category.name}|${categoryLink}`;
                if (!seenEntries.has(categoryKey)) {
                    seenEntries.add(categoryKey);
                    menuEntries.push({
                        id: String(category.id || category.name).trim(),
                        name: String(category.name).trim(),
                        link: categoryLink,
                        category: categoryLabel,
                        groupPath: buildGroupPath(categoryLabel, category.name),
                        isPluginCategory,
                    });
                }
            }

            (Array.isArray(category?.subcategories) ? category.subcategories : []).forEach((subcategory) => {
                if (!subcategory?.name || !subcategory?.link) {
                    return;
                }

                const subcategoryLink = resolveMenuLink(
                    subcategory.link,
                    loggedIn,
                    vertical,
                    subcategory.search ? 'search' : category.id,
                    subcategory.id,
                    subcategory.customLink ? subcategory.link : false,
                );
                const subcategoryKey = `${categoryLabel}|${subcategory.name}|${subcategoryLink}`;
                if (!seenEntries.has(subcategoryKey)) {
                    seenEntries.add(subcategoryKey);
                    menuEntries.push({
                        id: String(subcategory.id || subcategory.name).trim(),
                        name: String(subcategory.name).trim(),
                        link: subcategoryLink,
                        category: categoryLabel,
                        groupPath: buildGroupPath(category.name, subcategory.name),
                        isPluginCategory,
                        keywords: Array.isArray(subcategory.keywords) ? subcategory.keywords : [],
                    });
                }
            });
        });
    });

    return menuEntries;
};

const SearchBar = ({searchCallback='testSearch'}) => {

    const searchInputRef = useRef(null);
    const suppressAutocompleteOnNextFocusRef = useRef(false);

    const {user} = useUserContext();
    const headerMenu = useHeaderMenu();

    const [searchQuery, setSearchQuery] = useState(null);
    const [categorySelected, setCategorySelected] = useState(getCategoryFromLocation);
    const [categoryByUrl, setCategoryByUrl] = useState(getCategoryFromLocation);
    const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
    const [isAutocompleteSuppressed, setIsAutocompleteSuppressed] = useState(false);
    const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState(-1);

    const searchTokens = useMemo(() => getSearchTokens(searchQuery), [searchQuery]);

    const menuAutocompleteSource = useMemo(() => buildMenuAutocompleteSource(headerMenu, !!user), [headerMenu, user]);

    const menuMatches = useMemo(() => {
        if (searchTokens.length === 0) {
            return [];
        }

        return menuAutocompleteSource
            .map((menuEntry, index) => ({ ...menuEntry, originalIndex: index }))
            .filter((menuEntry) => {
                const searchableValues = getSearchableMenuValues(menuEntry);

                return searchTokens.every((token) => (
                    searchableValues.some((value) => value.includes(token))
                ));
            })
            .sort((a, b) => {
                const scoreA = calculateMatchScore(a, searchTokens);
                const scoreB = calculateMatchScore(b, searchTokens);
                if (scoreA !== scoreB) {
                    return scoreB - scoreA;
                }
                return a.originalIndex - b.originalIndex;
            })
            .slice(0, MAX_AUTOCOMPLETE_MATCHES);
    }, [menuAutocompleteSource, searchTokens]);

    const shouldShowMenuAutocomplete = isSearchInputFocused && !isAutocompleteSuppressed && menuMatches.length > 0;

    useEffect(() => {
        if (menuMatches.length === 0) {
            setActiveAutocompleteIndex(-1);
            return;
        }
        if (activeAutocompleteIndex >= menuMatches.length) {
            setActiveAutocompleteIndex(menuMatches.length - 1);
        }
    }, [activeAutocompleteIndex, menuMatches]);

    const clearInterval = useRafInterval(() => {
        setCategoryByUrl(getCategoryFromLocation());
    }, 100);

    useEffect(() => {
        return () => {
            clearInterval();
        };
    }, [clearInterval]);

    useEffect(() => {
        if (searchQuery === null || searchQuery === undefined) return;

        const categoryLocal = getCategoryFromLocation();

        if (searchCallback && window[searchCallback] && window && typeof window[searchCallback] === 'function') {
            window[searchCallback](searchQuery, categoryLocal);
        }
    }, [searchQuery]);

    useEffect(() => {
        setCategorySelected(categoryByUrl);
    }, [categoryByUrl, setCategorySelected]);

    useEffect(() => {
        if (!window.location.pathname.includes('/search')) {
            return;
        }

        let searchTerm = '';

        const parts = window.location.pathname.split('/');

        if (parts[1] === 'search' && parts[2]) {
            searchTerm = decodeURIComponent(parts[2]);
        } else if (parts[1] === 'search' && !parts[2]) {
            searchTerm = '';
        }
        searchTerm = searchTerm.replace('+', ' ');
        if (searchTerm.length > 0) {
            setSearchQuery(searchTerm);
        }
        if (searchInputRef && searchInputRef.current) {
            setIsAutocompleteSuppressed(true);
            suppressAutocompleteOnNextFocusRef.current = true;
            searchInputRef.current.focus();
        }
    }, []);

    const dismissAutocomplete = useCallback(({ blurInput = false } = {}) => {
        setIsSearchInputFocused(false);
        setActiveAutocompleteIndex(-1);
        if (blurInput && searchInputRef.current) {
            searchInputRef.current.blur();
        }
    }, []);

    const handleSearch = (event) => {
        if (event) {
            event.preventDefault();
        }

        const exactPluginMatch = findExactPluginMenuMatch(menuAutocompleteSource, searchQuery);
        if (exactPluginMatch) {
            navigateToAutocompleteMatch(exactPluginMatch);
            return;
        }

        setIsAutocompleteSuppressed(true);
        dismissAutocomplete({ blurInput: true });
        searchAssets(searchQuery, categorySelected, !!user);
    };

    const navigateToAutocompleteMatch = (match) => {
        if (!match?.link) {
            return;
        }
        setIsAutocompleteSuppressed(true);
        dismissAutocomplete({ blurInput: true });
        setSearchQuery(match.name);
        window.location.href = match.link;
    };

    const handleInputKeyDown = (event) => {
        if (!shouldShowMenuAutocomplete) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveAutocompleteIndex((prev) => (
                prev < menuMatches.length - 1 ? prev + 1 : 0
            ));
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveAutocompleteIndex((prev) => (
                prev > 0 ? prev - 1 : menuMatches.length - 1
            ));
            return;
        }

        if (event.key === 'Enter' && activeAutocompleteIndex >= 0) {
            event.preventDefault();
            navigateToAutocompleteMatch(menuMatches[activeAutocompleteIndex]);
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            dismissAutocomplete();
        }
    };

    return (
        <form className={`search-bar`} onSubmit={handleSearch} >
            <input
                type="text"
                value={searchQuery}
                onChange={({target}) => {
                    setSearchQuery(target.value);
                    setIsAutocompleteSuppressed(false);
                    setActiveAutocompleteIndex(-1);
                }}
                name="query"
                placeholder={'Search "new" for latest content'}
                spellCheck="True"
                autoComplete="off"
                id="header-search-bar-input"
                dir="auto"
                ref={searchInputRef}
                onFocus={() => {
                    setIsSearchInputFocused(true);
                    setActiveAutocompleteIndex(-1);
                    if (suppressAutocompleteOnNextFocusRef.current) {
                        suppressAutocompleteOnNextFocusRef.current = false;
                        return;
                    }
                    setIsAutocompleteSuppressed(false);
                }}
                onBlur={() => {
                    window.setTimeout(() => {
                        dismissAutocomplete();
                    }, 120);
                }}
                onKeyDown={handleInputKeyDown}
                className={`search-bar-input`}
            />
            <span
                className={`close glyphicon glyphicon-cross ${searchQuery?'visible':''}`}
                onClick={() => {
                    setSearchQuery('');
                }}
            />
            <button type="submit" className="form-btn btn-search">
                <span className="glyphicon glyphicon-search" />
            </button>

            {shouldShowMenuAutocomplete &&
            <div className="search-bar-autocomplete" role="listbox" aria-label="Menu suggestions">
                {menuMatches.map((menuMatch, index) =>
                    <a
                        key={`menu-autocomplete-${menuMatch.link}-${menuMatch.category}-${menuMatch.id}`}
                        href={menuMatch.link}
                        className={`search-bar-autocomplete-item ${activeAutocompleteIndex === index ? 'active' : ''}`}
                        role="option"
                        aria-selected={activeAutocompleteIndex === index}
                        onMouseEnter={() => {
                            setActiveAutocompleteIndex(index);
                        }}
                        onClick={() => {
                            setSearchQuery(menuMatch.name);
                        }}
                    >
                        <span className="search-bar-autocomplete-item-name">
                            {renderHighlightedText(resolveAutocompleteDisplayLabel(menuMatch), searchTokens)}
                        </span>
                        <span className={`search-bar-autocomplete-item-type ${menuMatch.isPluginCategory ? 'is-plugins' : ''}`}>
                            {renderHighlightedText(menuMatch.category, searchTokens)}
                        </span>
                    </a>
                )}
            </div>
            }
        </form>
    );
};

export default forwardRef(SearchBar);
